import { useEffect, useRef, useState } from 'react';
import { Wordmark } from '../brand';
import { persistLang } from '../i18n';
import { useHref, useLang } from '../lang';
import { PATHS, pathFor, type Route } from '../routes';

export const NAV = [
  ['waste', 'nav.waste'],
  ['vision', 'nav.vision'],
] as const;

export const FOOTER_NAV = [
  ['waste', 'nav.waste'],
  ['vision', 'nav.vision'],
  ['privacy', 'nav.privacy'],
  ['terms', 'nav.terms'],
] as const;

export function Header({ route }: { route: Route }) {
  const { t } = useLang();
  const href = useHref();
  const [open, setOpen] = useState(false);
  const menuBtn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add('no-scroll');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        menuBtn.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('no-scroll');
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => setOpen(false);
  const links = (onClick?: () => void) =>
    NAV.map(([r, key]) => (
      <a key={r} href={href(PATHS[r])} aria-current={route === r ? 'page' : undefined} onClick={onClick}>
        {t(key)}
      </a>
    ));

  return (
    <header className="header">
      <div className="header-bar frame">
        <a href={href(PATHS.home)} className="header-home" aria-current={route === 'home' ? 'page' : undefined}>
          <Wordmark />
          <span className="visually-hidden">{t('brand.home')}</span>
        </a>
        <nav className="header-nav" aria-label={t('nav.label')}>
          {links()}
        </nav>
        <div className="header-end">
          <LangSwitch route={route} />
          {route !== 'demo' && (
            <a className="btn btn-primary btn-sm header-cta" href={href(PATHS.demo)}>
              {t('nav.cta')}
            </a>
          )}
          <button
            ref={menuBtn}
            type="button"
            className="menu-btn"
            aria-expanded={open}
            aria-controls="drawer"
            onClick={() => setOpen((v) => !v)}
          >
            <span className="menu-icon" aria-hidden="true" />
            <span className="visually-hidden">{open ? t('nav.close') : t('nav.menu')}</span>
          </button>
        </div>
      </div>
      <div id="drawer" className={`drawer ${open ? 'is-open' : ''}`} inert={!open}>
        <nav className="drawer-nav frame" aria-label={t('nav.menu')}>
          {links(close)}
          <div className="drawer-actions">
            <a className="btn btn-primary" href={href(PATHS.demo)} onClick={close}>
              {t('nav.cta')}
            </a>
            <LangSwitch route={route} />
          </div>
        </nav>
      </div>
    </header>
  );
}

export function LangSwitch({ route, tone = 'ink' }: { route: Route; tone?: 'ink' | 'paper' }) {
  const { t, lang } = useLang();
  const [extra, setExtra] = useState('');
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    q.delete('lang');
    const rest = q.toString();
    setExtra(`${rest ? `?${rest}` : ''}${window.location.hash}`);
  }, []);
  const enHref = `${pathFor(route, 'en')}${extra}`;
  const arHref = `${pathFor(route, 'ar')}${extra}`;
  return (
    <div className={`lang lang-${tone}`} role="group" aria-label={t('nav.lang')}>
      <a
        href={enHref}
        lang="en"
        hrefLang="en"
        aria-current={lang === 'en' ? 'true' : undefined}
        title={t('nav.lang.switchToEn')}
        onClick={() => persistLang('en')}
      >
        EN
      </a>
      <a
        href={arHref}
        lang="ar"
        hrefLang="ar"
        aria-current={lang === 'ar' ? 'true' : undefined}
        title={t('nav.lang.switchToAr')}
        onClick={() => persistLang('ar')}
      >
        عربي
      </a>
    </div>
  );
}
